import { formatDuration } from "@shared/common/utils/luxonHelpers";
import { RetShiftAction, RetShiftActionType } from "@opstel/query/models/retShift";
import React, { useMemo, type JSX } from "react";
import EndIcon from "@shared/assets/eds-custom-icons/Stop.svg?react";
import { HighlightedText } from "@shared/common/components/HighlightedText";

interface CSSGridTableProps {
  readonly actions: RetShiftAction[];
  readonly trainNumberSearchString?: string;
}

function HeaderCell({ children }: Readonly<React.PropsWithChildren>): JSX.Element {
  return (
    <th
      role="columnheader"
      className="border-b border-emphasis p-xs text-left text-m font-bold text-emphasis last:pr-0"
    >
      {children}
    </th>
  );
}

function DataCell({ children }: Readonly<React.PropsWithChildren>): JSX.Element {
  return <td className="border-b border-default p-xs text-m font-normal last:pr-0">{children}</td>;
}

export function RetShiftActions({
  actions: unsortedActions,
  trainNumberSearchString,
}: CSSGridTableProps): JSX.Element {
  const actions = useMemo(() => {
    return [...unsortedActions]
      .sort((a, b) => a.start.toMillis() - b.start.toMillis())
      .filter(
        (action) =>
          action.trainNumber.includes(trainNumberSearchString || "") ||
          action.trainNumberDeparture.includes(trainNumberSearchString || ""),
      );
  }, [trainNumberSearchString, unsortedActions]);

  return (
    <table role="table" className="w-full border-collapse border-l-[3px] border-info text-content">
      <tbody>
        <tr className="grid grid-cols-[minmax(150px,1fr)_minmax(330px,1fr)_minmax(180px,1fr)_minmax(130px,2fr)_minmax(130px,2fr)] pl-l">
          <HeaderCell>Klus</HeaderCell>
          <HeaderCell>Duur</HeaderCell>
          <HeaderCell>Spoor</HeaderCell>
          <HeaderCell>Treinnr. aankomst</HeaderCell>
          <HeaderCell>Treinnr. vertrek</HeaderCell>
        </tr>
        {actions.map((action) => {
          const duration = action.end.diff(action.start);
          const formattedDuration = formatDuration(duration, "%h%M%S");
          return (
            <tr
              key={action.start.toMillis()}
              className="grid grid-cols-[minmax(150px,1fr)_minmax(330px,1fr)_minmax(180px,1fr)_minmax(130px,2fr)_minmax(130px,2fr)] pl-l"
            >
              <DataCell>
                <div className="flex items-center gap-s text-emphasis">
                  {action.type && IconMap[action.type]}
                  <span>{action.description}</span>
                </div>
              </DataCell>
              <DataCell>
                {`${action.start.toFormat("ccc HH:mm:ss")} t/m ${action.end.toFormat("ccc HH:mm:ss")}`}{" "}
                <span className="text-subtle">({formattedDuration})</span>
              </DataCell>
              <DataCell>
                {action.fromTrack === action.track
                  ? action.fromTrack
                  : `${action.fromTrack} → ${action.track}`}
              </DataCell>
              <DataCell>
                <HighlightedText
                  text={action.trainNumber}
                  highlightedText={trainNumberSearchString}
                />
              </DataCell>
              <DataCell>
                <HighlightedText
                  text={action.trainNumberDeparture}
                  highlightedText={trainNumberSearchString}
                />
              </DataCell>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const iconStyle = {
  width: "24px",
  height: "24px",
  strokeWidth: "0px",
};

const IconMap: Record<RetShiftActionType, React.JSX.Element> = {
  ["MOVE"]: <tractie-icon name="arrow-right" style={iconStyle} />,
  ["VGM"]: <tractie-icon name="list" style={iconStyle} />,
  ["GVGM"]: <tractie-icon name="list" style={iconStyle} />,
  ["VGMS"]: <tractie-icon name="list" style={iconStyle} />,
  ["WALKING"]: <tractie-icon name="walk" style={iconStyle} />,
  ["SCHAFT"]: <tractie-icon name="restaurant" style={iconStyle} />,
  ["COMBINE"]: <tractie-icon name="combine" style={{ ...iconStyle, transform: "rotate(90deg)" }} />,
  ["SPLIT"]: <tractie-icon name="divide" style={{ ...iconStyle, transform: "rotate(90deg)" }} />,
  ["VOORTIJD"]: <tractie-icon name="play" style={iconStyle} />,
  ["NATIJD"]: <EndIcon style={iconStyle} />,
  ["UNKNOWN"]: <div />,
};
